/**
 * How the user reached a topic page. A click that leads to a topic leaves a
 * note; the topic page takes it on arrival and keeps it until it records the
 * open. The note lives in memory, so a new tab or a reload starts without one.
 */
export type TopicEntrySource =
    | "citation"
    | "search"
    | "portal"
    | "favorite"
    | "history"
    | "internal_link"
    | "direct_unknown";

type Note = {
    topicId: string;
    source: TopicEntrySource;
    alreadyCounted: boolean;
    at: number;
};

/** A note older than this belongs to a click that did not lead to the page. */
export const NOTE_TTL_MS = 10_000;

let pending: Note | null = null;

/** The island's current topic, so its links to the full page know whether the island counted it. */
let islandVisit: {topicId: string; counted: boolean} | null = null;

const normalize = (topicId: string) => topicId.trim().toLowerCase();

/**
 * Record how the next open of `topicId` was reached. `alreadyCounted` marks
 * a page that continues a view already recorded (island → full page).
 */
export function noteTopicEntry(
    topicId: string | null | undefined,
    source: TopicEntrySource,
    options: {alreadyCounted?: boolean} = {}
) {
    if (!topicId) return;
    pending = {
        topicId: normalize(topicId),
        source,
        alreadyCounted: options.alreadyCounted ?? false,
        at: Date.now(),
    };
}

/**
 * Take the note for `topicId`, if a fresh one exists. A note is used once;
 * a stale or mismatching note is discarded.
 */
export function takeTopicEntry(
    topicId: string
): {source: TopicEntrySource; alreadyCounted: boolean} | null {
    const note = pending;
    pending = null;
    if (!note) return null;
    if (note.topicId !== normalize(topicId)) return null;
    if (Date.now() - note.at > NOTE_TTL_MS) return null;
    return {source: note.source, alreadyCounted: note.alreadyCounted};
}

/** The island arrived at `topicId`; it has not counted it yet. */
export function startIslandVisit(topicId: string) {
    islandVisit = {topicId: normalize(topicId), counted: false};
}

/** The island recorded the open of `topicId`. */
export function markIslandCounted(topicId: string) {
    if (islandVisit?.topicId === normalize(topicId)) islandVisit.counted = true;
}

/**
 * A link from the island to the full page. The page continues the island's
 * view only if the island counted it; otherwise the page counts the open.
 */
export function noteIslandToPage(topicId: string | null | undefined) {
    if (!topicId) return;
    const counted = islandVisit?.topicId === normalize(topicId) && islandVisit.counted;
    noteTopicEntry(topicId, "citation", {alreadyCounted: counted});
}

/** Test seam. */
export function clearTopicEntryNote() {
    pending = null;
    islandVisit = null;
}
